import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Landmark, UserPlus } from "lucide-react";
import { getApiErrorMessage } from "@/lib/api";

export default function Signup() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Account created! Let's complete your KYC.");
      navigate("/onboarding", { replace: true });
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="smifs-hero-gradient relative hidden flex-col justify-between p-10 text-white lg:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
            <Landmark className="h-5 w-5 text-white" />
          </div>
          <span className="font-heading text-xl">SMIFS</span>
        </div>
        <div className="max-w-md">
          <h1 className="font-heading text-4xl leading-tight">Begin your investing journey</h1>
          <p className="mt-4 text-white/80">Create your account, complete a quick KYC-lite check, and start investing in mutual funds across 65+ AMCs.</p>
        </div>
        <div className="text-xs text-white/50">Orders are processed via BSE StAR MF; timelines may vary by AMC/RTA.</div>
      </div>

      <div className="flex items-center justify-center p-3 sm:p-6">
        <div className="glass-card w-full max-w-md p-6 sm:p-8">
          <h2 className="font-heading text-2xl text-[var(--brand)]">Create your account</h2>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">It takes less than a minute.</p>

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="full_name">Full name</Label>
              <Input id="full_name" required value={form.full_name} onChange={update("full_name")} data-testid="signup-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="signup-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Mobile number</Label>
              <Input id="phone" required value={form.phone} onChange={update("phone")} data-testid="signup-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={10} value={form.password} onChange={update("password")} data-testid="signup-password-input" />
              <p className="text-xs text-[var(--ink-muted)]">10+ characters with upper-case, lower-case and a number.</p>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="signup-submit-button">
              <UserPlus className="mr-2 h-4 w-4" /> {loading ? "Creating account..." : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[var(--ink-muted)]">
            Already have an account? <Link to="/login" className="font-medium text-[var(--cta)]" data-testid="go-to-login-link">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
